import confetti from 'canvas-confetti';
import type { GameResult } from '../types';
import { copyToClipboard } from '../utils/helpers';
import { resultToText, resultToMarkdown, exportResultCSV, exportResultTxt } from '../utils/csv';

interface ResultModalCallbacks {
  onPlayAgain?: () => void;
  onNewGame?: () => void;
}

export class ResultModal {
  private el: HTMLElement;
  private result: GameResult;
  private className: string;
  private callbacks: ResultModalCallbacks;
  private revealIndex = 0;
  private revealTimer: ReturnType<typeof setTimeout> | null = null;
  private isAutoReveal = true;

  constructor(
    container: HTMLElement,
    result: GameResult,
    className: string,
    callbacks: ResultModalCallbacks = {},
  ) {
    this.result = result;
    this.className = className;
    this.callbacks = callbacks;

    this.el = document.createElement('div');
    this.el.className = 'result-modal-overlay';
    container.appendChild(this.el);

    this.render();
    this.fireConfetti();
  }

  private render() {
    const { result, className } = this;

    this.el.innerHTML = `
      <div class="result-modal" role="dialog" aria-modal="true">

        <div class="result-hero">
          <div class="result-hero-main">
            <span class="result-hero-icon">${this.getHeaderIcon()}</span>
            <div>
              <h2 class="result-heading">${this.getHeading()}</h2>
              ${className ? `<p class="result-class">${className}</p>` : ''}
            </div>
          </div>
          <div class="result-reveal-bar">
            <button class="btn-reveal" id="btn-reveal">공개</button>
            <label class="auto-toggle">
              <input type="checkbox" id="auto-reveal" ${this.isAutoReveal ? 'checked' : ''} />
              <span>자동</span>
            </label>
          </div>
        </div>

        <div class="result-content" id="result-content">
          ${this.renderResults()}
        </div>

        <div class="result-footer">
          <div class="result-nav-row">
            <button class="btn-secondary" id="btn-again">다시 하기</button>
            <button class="btn-primary" id="btn-new">새 게임</button>
          </div>
          <details class="result-export-panel">
            <summary>결과 복사/저장</summary>
            <div class="result-export-row">
              <button class="btn-export" id="btn-copy" title="텍스트 복사">텍스트 복사</button>
              <button class="btn-export" id="btn-copy-md" title="마크다운 복사">MD 복사</button>
              <button class="btn-export" id="btn-dl-txt" title="TXT 저장">TXT 저장</button>
              <button class="btn-export" id="btn-dl-csv" title="CSV 저장">CSV 저장</button>
            </div>
          </details>
        </div>

      </div>
    `;

    this.bindEvents();

    const items = this.el.querySelectorAll<HTMLElement>('.result-item');
    items.forEach((item) => item.classList.add('hidden'));
    this.revealIndex = 0;
    this.revealNext();
    this.startAutoReveal();
  }

  private getHeaderIcon(): string {
    const { mode } = this.result;
    if (mode === 'single') return '🎊';
    if (mode === 'multi') return '🏆';
    if (mode === 'team') return '👥';
    return '🎁';
  }

  private getHeading(): string {
    const { mode, config } = this.result;
    if (mode === 'single') return '당첨!';
    if (mode === 'multi') return `${config.pickCount}명 선정!`;
    if (mode === 'team') return `${config.teamCount}팀 완성!`;
    const rewardCount = (config.rewards ?? []).reduce((sum, reward) => sum + Math.max(0, reward.count || 0), 0);
    return `${rewardCount}개 보상 지급!`;
  }

  private renderResults(): string {
    const { result } = this;

    if (result.mode === 'team' && result.teams) {
      return `<div class="result-grid team-grid">
        ${result.teams.map((t, i) => `
          <div class="result-item team-item" data-i="${i}">
            <span class="team-label">${t.teamName}</span>
            <div class="team-members">
              ${t.members.map((m) => `<span class="member-chip">${m}</span>`).join('')}
            </div>
          </div>
        `).join('')}
      </div>`;
    }

    const medals = ['🥇', '🥈', '🥉'];
    const targetRank = result.config.winnerRule === 'last'
      ? result.rankings.length
      : result.config.winnerRule === 'custom'
        ? Math.max(1, Math.min(result.config.winnerRank ?? 1, result.rankings.length))
        : 1;

    const rankings = result.mode === 'single'
      ? result.rankings.filter((r) => r.rank === targetRank).slice(0, 1)
      : result.mode === 'reward'
        ? result.rankings.filter((r) => Boolean(r.reward))
      : result.rankings.slice(0, result.config.pickCount);

    if (result.mode === 'reward') {
      return `
        <div class="reward-result-list">
          ${rankings.map((r, i) => `
            <div class="result-item reward-result-card" data-i="${i}" style="--item-color:${r.color}">
              <span class="reward-result-rank">${r.rank}위</span>
              <div class="reward-result-main">
                <strong>${r.name}</strong>
                <span>${r.reward}</span>
              </div>
            </div>
          `).join('')}
        </div>
      `;
    }

    // Single winner — big card
    if (result.mode === 'single' || rankings.length < 3) {
      return rankings.map((r, i) => `
        <div class="result-item rank-item ${r.rank === 1 ? 'gold' : ''}" data-i="${i}"
             style="--item-color:${r.color}">
          <span class="result-medal">${medals[r.rank - 1] ?? `${r.rank}위`}</span>
          <span class="result-name">${r.name}</span>
          ${r.reward ? `<span class="result-reward">${r.reward}</span>` : ''}
        </div>
      `).join('');
    }

    // Multi mode — grid for 4+, podium for 1-3
    const top3 = rankings.slice(0, 3);
    const rest = rankings.slice(3);

    const gridHtml = rest.length > 0 ? `
      <div class="result-grid">
        ${rest.map((r, i) => `
          <div class="result-item result-grid-item" data-i="${i + 3}" style="--item-color:${r.color}">
            <span class="grid-rank">${r.rank}위</span>
            <span class="grid-name">${r.name}</span>
          </div>
        `).join('')}
      </div>
    ` : '';

    const podiumOrder = [top3[2], top3[1], top3[0]];
    const podiumHtml = `
      <div class="podium-wrapper">
        ${podiumOrder.filter(Boolean).map((r) => `
          <div class="result-item podium-slot" data-rank="${r.rank}" style="--item-color:${r.color}">
            <div class="podium-card">
              <div class="podium-medal">${medals[r.rank - 1]}</div>
              <div class="podium-name">${r.name}</div>
            </div>
            <div class="podium-stand">${r.rank}위</div>
          </div>
        `).join('')}
      </div>
    `;

    return gridHtml + podiumHtml;
  }

  private revealNext() {
    const items = this.el.querySelectorAll<HTMLElement>('.result-item.hidden');
    if (items.length === 0) return;

    items[0].classList.remove('hidden');
    items[0].classList.add('revealed');
    this.revealIndex++;

    const remaining = this.el.querySelectorAll('.result-item.hidden').length;
    if (remaining === 0) {
      this.fireConfetti();
      // Hide reveal bar when all revealed
      const bar = this.el.querySelector<HTMLElement>('.result-reveal-bar');
      if (bar) bar.style.opacity = '0.4';
    }
  }

  private fireConfetti() {
    const duration = 2200;
    const end = Date.now() + duration;
    const frame = () => {
      confetti({ particleCount: 4, angle: 60, spread: 58, origin: { x: 0, y: 0.8 }, colors: ['#5EEAD4', '#FB7185', '#FBBF24', '#A78BFA'] });
      confetti({ particleCount: 4, angle: 120, spread: 58, origin: { x: 1, y: 0.8 }, colors: ['#5EEAD4', '#FB7185', '#FBBF24', '#A78BFA'] });
      if (Date.now() < end) requestAnimationFrame(frame);
    };
    frame();
  }

  private bindEvents() {
    this.el.querySelector('#btn-reveal')?.addEventListener('click', () => this.revealNext());

    const autoCheck = this.el.querySelector<HTMLInputElement>('#auto-reveal');
    autoCheck?.addEventListener('change', () => {
      this.isAutoReveal = autoCheck.checked;
      if (this.isAutoReveal) this.startAutoReveal();
      else this.stopAutoReveal();
    });

    document.addEventListener('keydown', this.handleKey);

    this.el.querySelector('#btn-copy')?.addEventListener('click', async () => {
      const ok = await copyToClipboard(resultToText(this.result));
      this.showToast(ok ? '복사됨!' : '복사 실패');
    });

    this.el.querySelector('#btn-copy-md')?.addEventListener('click', async () => {
      const ok = await copyToClipboard(resultToMarkdown(this.result));
      this.showToast(ok ? '마크다운 복사됨!' : '복사 실패');
    });

    this.el.querySelector('#btn-dl-txt')?.addEventListener('click', () => {
      exportResultTxt(this.result, this.className);
    });

    this.el.querySelector('#btn-dl-csv')?.addEventListener('click', () => {
      exportResultCSV(this.result, this.className);
    });

    this.el.querySelector('#btn-again')?.addEventListener('click', () => {
      this.callbacks.onPlayAgain?.();
    });

    this.el.querySelector('#btn-new')?.addEventListener('click', () => {
      this.callbacks.onNewGame?.();
    });
  }

  private startAutoReveal() {
    if (this.revealTimer) return;
    const reveal = () => {
      const hidden = this.el.querySelectorAll<HTMLElement>('.result-item.hidden');
      if (hidden.length === 0) { this.stopAutoReveal(); return; }
      const isPodium = hidden[0].classList.contains('podium-slot');
      const delay = isPodium ? 1200 : 120;
      this.revealNext();
      this.revealTimer = setTimeout(reveal, delay);
    };
    this.revealTimer = setTimeout(reveal, 600);
  }

  private stopAutoReveal() {
    if (this.revealTimer) {
      clearTimeout(this.revealTimer);
      this.revealTimer = null;
    }
  }

  private handleKey = (e: KeyboardEvent) => {
    if (e.key === ' ' || e.key === 'Enter') {
      e.preventDefault();
      this.revealNext();
    }
  };

  private showToast(msg: string) {
    const toast = document.createElement('div');
    toast.className = 'toast';
    toast.textContent = msg;
    document.body.appendChild(toast);
    requestAnimationFrame(() => toast.classList.add('show'));
    setTimeout(() => { toast.classList.remove('show'); setTimeout(() => toast.remove(), 400); }, 2000);
  }

  destroy() {
    this.stopAutoReveal();
    document.removeEventListener('keydown', this.handleKey);
    this.el.remove();
  }
}
